import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 20,
    fontFamily: 'Helvetica',
    fontSize: 7.5,
    color: '#1e293b'
  },
  headerBanner: {
    backgroundColor: '#1e293b',
    color: '#ffffff',
    padding: 8,
    borderRadius: 3,
    marginBottom: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  headerTitle: {
    fontSize: 11.5,
    fontWeight: 'bold',
    color: '#ffffff',
    textTransform: 'uppercase'
  },
  headerSubtitle: {
    fontSize: 7.5,
    color: '#94a3b8',
    marginTop: 2
  },
  badge: {
    backgroundColor: '#334155',
    color: '#ffffff',
    paddingVertical: 3,
    paddingHorizontal: 6,
    borderRadius: 2,
    fontSize: 7.5,
    fontWeight: 'bold'
  },
  grid2: {
    flexDirection: 'row',
    gap: 6,
    marginBottom: 6
  },
  panel: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 3,
    padding: 5,
    backgroundColor: '#f8fafc'
  },
  panelTitle: {
    fontSize: 7.5,
    fontWeight: 'bold',
    color: '#1e293b',
    borderBottomWidth: 1,
    borderBottomColor: '#cbd5e1',
    paddingBottom: 2,
    marginBottom: 4,
    textTransform: 'uppercase'
  },
  fieldRow: {
    flexDirection: 'row',
    marginBottom: 2.2,
    alignItems: 'flex-start'
  },
  fieldLabel: {
    fontSize: 6.8,
    fontWeight: 'bold',
    color: '#475569',
    width: '45%'
  },
  fieldValue: {
    fontSize: 6.8,
    color: '#0f172a',
    width: '55%'
  },
  passText: {
    color: '#15803d',
    fontWeight: 'bold'
  },
  signaturesPanel: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 4,
    borderTopWidth: 1,
    borderTopColor: '#cbd5e1',
    paddingTop: 6
  },
  signatureBox: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 3,
    padding: 5,
    backgroundColor: '#ffffff'
  },
  footer: {
    marginTop: 6,
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    paddingTop: 4,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  footerText: {
    fontSize: 5.8,
    color: '#64748b'
  }
});

const InstallationCommissioningDecommissioningPDF = ({ formData = {} }) => {
  const contractor = formData.contractorDetails || {};
  const site = formData.siteDetails || {};
  const scope = formData.scopeOfWorks || {};
  const safe = formData.safeIsolationAndEnvironment || {};
  const comm = formData.commissioningReadings || {};
  const hand = formData.handoverAndSignatures || {};

  return (
    <Document>
      <Page size="A4" style={styles.page} orientation="portrait">
        {/* Banner */}
        <View style={styles.headerBanner}>
          <View>
            <Text style={styles.headerTitle}>Installation, Commissioning & Decommissioning Record</Text>
            <Text style={styles.headerSubtitle}>Multi-Trade Safe Isolation, Hazardous Fluid Recovery & Statutory Handover</Text>
          </View>
          <View style={styles.badge}>
            <Text>HANDOVER CERTIFICATE</Text>
          </View>
        </View>

        {/* 1 & 2: Contractor & Site */}
        <View style={styles.grid2}>
          <View style={styles.panel}>
            <Text style={styles.panelTitle}>1. Executing Contractor</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Company:</Text>
              <Text style={styles.fieldValue}>{contractor.companyName || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Lead Engineer:</Text>
              <Text style={styles.fieldValue}>{contractor.engineerName || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Competency / Card ID:</Text>
              <Text style={styles.fieldValue}>{contractor.tradeCompetencyNo || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Telephone:</Text>
              <Text style={styles.fieldValue}>{contractor.telephone || 'N/A'}</Text>
            </View>
          </View>

          <View style={styles.panel}>
            <Text style={styles.panelTitle}>2. Site / Facility & Client</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Client Name:</Text>
              <Text style={styles.fieldValue}>{site.clientName || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Site Address:</Text>
              <Text style={styles.fieldValue}>{site.siteAddress || 'N/A'}, {site.postCode || ''}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Date Works Executed:</Text>
              <Text style={styles.fieldValue}>{site.workDate || 'N/A'}</Text>
            </View>
          </View>
        </View>

        {/* 3: Scope of Works */}
        <View style={styles.panel}>
          <Text style={styles.panelTitle}>3. Scope of Mechanical & Electrical Works</Text>
          <View style={styles.grid2}>
            <View style={{ flex: 1 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Activity Type:</Text>
                <Text style={styles.fieldValue}>{scope.workActivityType || 'Installation'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Equipment Category:</Text>
                <Text style={styles.fieldValue}>{scope.equipmentCategory || 'HVAC'}</Text>
              </View>
            </View>
            <View style={{ flex: 1 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Make & Model:</Text>
                <Text style={styles.fieldValue}>{scope.equipmentMakeModel || 'N/A'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Serial No. / Location:</Text>
                <Text style={styles.fieldValue}>{scope.serialNumber || 'N/A'} ({scope.locationInBuilding || 'Plant Room'})</Text>
              </View>
            </View>
          </View>
        </View>

        {/* 4: Safe Isolation & Environment */}
        <View style={styles.panel}>
          <Text style={styles.panelTitle}>4. Safe Isolation & Environmental Fluid Recovery (F-Gas / Hazardous Waste)</Text>
          <View style={styles.grid2}>
            <View style={{ flex: 1 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Electrical Safe Isolation (LOTO):</Text>
                <Text style={[styles.fieldValue, styles.passText]}>{safe.safeIsolationCompleted || 'Verified'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Gas / Fuel Line Isolated & Capped:</Text>
                <Text style={styles.fieldValue}>{safe.gasOrFuelIsolated || 'N/A'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Refrigerant F-Gas Recovery:</Text>
                <Text style={styles.fieldValue}>{safe.fGasRecovered || 'N/A'}</Text>
              </View>
            </View>
            <View style={{ flex: 1 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Recovered F-Gas & Weight:</Text>
                <Text style={styles.fieldValue}>{safe.refrigerantTypeAndWeightKg || 'None'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Waste Transfer / Note Ref:</Text>
                <Text style={styles.fieldValue}>{safe.wasteTransferCertNumber || 'N/A'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Fluid Drainage Method:</Text>
                <Text style={styles.fieldValue}>{safe.glycolOrFluidDisposalMethod || 'Recycled'}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* 5: Commissioning Readings */}
        <View style={styles.panel}>
          <Text style={styles.panelTitle}>5. Commissioning Test Values & Performance Checks</Text>
          <View style={styles.grid2}>
            <View style={{ flex: 1 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Operating Pressure:</Text>
                <Text style={styles.fieldValue}>{comm.operatingPressureBar ? `${comm.operatingPressureBar} Bar` : 'N/A'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Flow Temperature:</Text>
                <Text style={styles.fieldValue}>{comm.flowTemperatureC ? `${comm.flowTemperatureC} °C` : 'N/A'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Electrical Supply & Current:</Text>
                <Text style={styles.fieldValue}>{comm.electricalVoltageAndAmps || '230V / 14A'}</Text>
              </View>
            </View>
            <View style={{ flex: 1 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Safety Interlocks Tested:</Text>
                <Text style={[styles.fieldValue, styles.passText]}>{comm.safetyInterlocksVerified || 'Yes - Tested'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Manufacturer Log Stamped:</Text>
                <Text style={[styles.fieldValue, styles.passText]}>{comm.manufacturersChecklistAttached || 'Yes - Benchmark'}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* 6: Handover Notes */}
        <View style={styles.panel}>
          <Text style={styles.panelTitle}>6. Handover Summary & Completion Status</Text>
          <View style={styles.fieldRow}>
            <Text style={styles.fieldLabel}>Completion Status:</Text>
            <Text style={[styles.fieldValue, { fontWeight: 'bold', color: '#1e293b' }]}>{hand.completionStatus || 'COMMISSIONED & OPERATIONAL'}</Text>
          </View>
          <Text style={{ fontSize: 6.5, color: '#334155', marginTop: 2 }}>
            {hand.engineerComments || 'Works completed in accordance with health & safety standards, manufacturer instructions, and statutory commissioning guidance.'}
          </Text>
        </View>

        {/* Signatures */}
        <View style={styles.signaturesPanel}>
          <View style={styles.signatureBox}>
            <Text style={styles.panelTitle}>Lead Commissioning / Decommissioning Engineer</Text>
            <View style={{ height: 32, justifyContent: 'center' }}>
              <Text style={{ fontSize: 7, fontWeight: 'bold' }}>{contractor.engineerName || 'Lead Engineer'}</Text>
              <Text style={{ fontSize: 6, color: '#64748b' }}>Accredited Handover Stamp</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Date:</Text>
              <Text style={styles.fieldValue}>{site.workDate || new Date().toISOString().split('T')[0]}</Text>
            </View>
          </View>

          <View style={styles.signatureBox}>
            <Text style={styles.panelTitle}>Client / Facility Handover Acceptance</Text>
            <View style={{ height: 32, justifyContent: 'center' }}>
              <Text style={{ fontSize: 7, fontWeight: 'bold' }}>{site.clientName || 'Client'}</Text>
              <Text style={{ fontSize: 6, color: '#64748b' }}>Plant handover accepted & signed</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Date:</Text>
              <Text style={styles.fieldValue}>{site.workDate || new Date().toISOString().split('T')[0]}</Text>
            </View>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            General Installation, Commissioning & Safe Decommissioning Statutory Record
          </Text>
          <Text style={styles.footerText}>
            Generated: {new Date().toLocaleDateString('en-GB')}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default InstallationCommissioningDecommissioningPDF;
